import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Polygon } from '@react-google-maps/api';

import { DeleteOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import {
  Button, Flex, Spin, Typography,
} from 'antd';
import { normalizeLatLng } from 'utils/places';
import useConfig from 'hooks/useConfig';
import Map from './Map';

const POLYGON_OPTIONS = {
  fillColor: '#000',
  fillOpacity: 0.4,
  strokeWeight: 2,
  strokeColor: '#111',
  clickable: true,
  editable: false,
  visible: true,
  zIndex: 1,
  draggable: false,
};

const DRAFT_POLYGON_OPTIONS = {
  ...POLYGON_OPTIONS,
  fillOpacity: 0.25,
  strokeColor: '#444',
};

function RegionInput({ value = [], onChange }) {
  const polygonsRef = useRef([]);
  const listenersRef = useRef({});
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const isDrawingRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPath, setDraftPath] = useState([]);
  const { defaultLat, defaultLong } = useConfig('general') || {};

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  const center = useMemo(() => {
    if (!value.length) {
      return { lat: defaultLat, lng: defaultLong };
    }
    const centers = value.map(calculateCenter);
    return calculateCenter(centers);

    function calculateCenter(points) {
      const sum = points.reduce(
        (acc, { lat, lng }) => ({ lat: acc.lat + lat, lng: acc.lng + lng }),
        { lat: 0, lng: 0 },
      );
      return { lat: sum.lat / points.length, lng: sum.lng / points.length };
    }
  }, [value, defaultLat, defaultLong]);

  const removePolygonListeners = useCallback((index) => {
    const listeners = listenersRef.current[index];
    if (!listeners) return;
    listeners.forEach((listener) => listener.remove());
    delete listenersRef.current[index];
  }, []);

  const emitChange = useCallback(() => {
    const paths = valueRef.current.map((_, index) => {
      const polygon = polygonsRef.current[index];
      if (!polygon) return null;
      return polygon.getPath().getArray().map(normalizeLatLng);
    });

    if (paths.some((path) => !path)) {
      return;
    }

    onChangeRef.current(paths);
  }, []);

  const clearSelections = useCallback(() => {
    polygonsRef.current.filter(Boolean).forEach((polygon) => polygon.setEditable(false));
    setSelectedIndex(null);
  }, []);

  const attachPolygonListeners = useCallback((polygon, index) => {
    removePolygonListeners(index);

    const listeners = [
      window.google.maps.event.addListener(polygon, 'click', () => {
        if (isDrawingRef.current) return;
        clearSelections();
        polygon.setEditable(true);
        setSelectedIndex(index);
      }),
    ];

    polygon.getPaths().forEach((path) => {
      listeners.push(window.google.maps.event.addListener(path, 'set_at', emitChange));
      listeners.push(window.google.maps.event.addListener(path, 'insert_at', emitChange));
      listeners.push(window.google.maps.event.addListener(path, 'remove_at', emitChange));
    });

    listenersRef.current[index] = listeners;
  }, [clearSelections, emitChange, removePolygonListeners]);

  const handlePolygonLoad = useCallback((polygon, index) => {
    polygonsRef.current[index] = polygon;
    attachPolygonListeners(polygon, index);
  }, [attachPolygonListeners]);

  const handlePolygonUnmount = useCallback((polygon, index) => {
    removePolygonListeners(index);
    if (polygonsRef.current[index] === polygon) {
      delete polygonsRef.current[index];
    }
  }, [removePolygonListeners]);

  useEffect(() => {
    Object.keys(listenersRef.current).forEach((key) => {
      const index = Number(key);
      if (index >= value.length) {
        removePolygonListeners(index);
      }
    });
    polygonsRef.current = polygonsRef.current.slice(0, value.length);
    if (selectedIndex !== null && selectedIndex >= value.length) {
      setSelectedIndex(null);
    }
  }, [value.length, removePolygonListeners, selectedIndex]);

  useEffect(() => () => {
    Object.keys(listenersRef.current).forEach((key) => {
      removePolygonListeners(Number(key));
    });
    polygonsRef.current = [];
  }, [removePolygonListeners]);

  const handleMapClick = useCallback((latLng) => {
    if (!isDrawingRef.current) return;
    setDraftPath((current) => [...current, normalizeLatLng(latLng)]);
  }, []);

  const handleStartDrawing = () => {
    clearSelections();
    setDraftPath([]);
    setIsDrawing(true);
  };

  const handleFinishDrawing = () => {
    if (draftPath.length < 3) return;
    const nextValue = [...value, draftPath];
    onChangeRef.current(nextValue);
    setDraftPath([]);
    setIsDrawing(false);
  };

  const handleCancelDrawing = () => {
    setDraftPath([]);
    setIsDrawing(false);
  };

  const handleDeleteSelected = () => {
    if (selectedIndex === null) return;
    onChangeRef.current(value.filter((_, index) => index !== selectedIndex));
    setSelectedIndex(null);
  };

  if (!(defaultLat || center?.lat)) {
    return <Spin />;
  }

  return (
    <>
      <Flex gap={8} align="center" wrap="wrap" style={{ marginBottom: 8 }}>
        <Button
          size="small"
          type={isDrawing ? 'primary' : 'default'}
          icon={<EditOutlined />}
          onClick={handleStartDrawing}
        >
          Draw polygon
        </Button>
        {isDrawing ? (
          <>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleFinishDrawing}
              disabled={draftPath.length < 3}
            >
              Finish polygon
            </Button>
            <Button size="small" onClick={handleCancelDrawing}>
              Cancel
            </Button>
            <Typography.Text type="secondary">
              Click on the map to place points (
              {draftPath.length}
              {' '}
              placed, minimum 3).
            </Typography.Text>
          </>
        ) : null}
        {selectedIndex !== null && !isDrawing ? (
          <Button
            size="small"
            type="primary"
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            danger
          >
            Delete polygon
          </Button>
        ) : null}
      </Flex>
      <Map
        height={300}
        zoom={10}
        center={center}
        onMapClick={handleMapClick}
        preventMapClick
      >
        {value.map((path, index) => (
          <Polygon
            // Remount on add/delete; stable across vertex edits (same index + length).
            // eslint-disable-next-line react/no-array-index-key
            key={`polygon-${index}-${value.length}`}
            path={path}
            options={POLYGON_OPTIONS}
            onLoad={(polygon) => handlePolygonLoad(polygon, index)}
            onUnmount={(polygon) => handlePolygonUnmount(polygon, index)}
          />
        ))}
        {draftPath.length > 0 ? (
          <Polygon
            path={draftPath}
            options={DRAFT_POLYGON_OPTIONS}
          />
        ) : null}
      </Map>
    </>
  );
}

RegionInput.serialize = (data) => ({
  type: 'MultiPolygon',
  coordinates: data.map((polygon) => {
    const points = polygon.map((point) => [point.lng, point.lat]);
    // Backend requirement: the first point should be repeated at end
    if (
      points[0][0] !== points[points.length - 1][0] ||
        points[0][1] !== points[points.length - 1][1]
    ) {
      points.push(points[0]);
    }
    return [points];
  }),
});
RegionInput.deserialize = (data = { coordinates: [] }) => data?.coordinates?.map((group) => group[0].map(([lng, lat]) => ({ lat, lng })));
export default RegionInput;
